/**
 * Formulaire d'ajout/édition d'une charge fixe
 * @module components/tresorerie/ChargesFixesForm
 */

import { useState, useEffect } from 'react';
import { useModalProtection } from '@/hooks/useModalProtection';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import type {
  ChargeFix,
  CreateChargeFixData,
  CategorieChargeFix,
  FrequenceChargeFix,
} from '@/types/charges-fixes.types';
import {
  CATEGORIES_LABELS,
  FREQUENCES_LABELS,
} from '@/types/charges-fixes.types';

// Schema de validation Zod
const chargeFixeSchema = z.object({
  nom: z.string().min(1, "Le nom est requis").max(100, "100 caractères max"),
  categorie: z.enum(['loyer', 'assurance', 'leasing', 'abonnement', 'energie', 'vehicules', 'emprunt', 'comptable', 'taxes', 'autre'] as const, { message: "Catégorie invalide" }),
  montant_ht: z.number().min(0, "Le montant ne peut pas être négatif").max(10_000_000, "Montant trop élevé"),
  taux_tva: z.number().min(0).max(100),
  frequence: z.enum(['mensuel', 'trimestriel', 'annuel'] as const, { message: "Fréquence invalide" }),
  jour_prelevement: z.number().int().min(1, "Jour entre 1 et 28").max(28, "Jour entre 1 et 28"),
});

interface ChargesFixesFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateChargeFixData) => Promise<void>;
  chargeFix?: ChargeFix | null;
}

/**
 * Formulaire modal pour créer ou éditer une charge fixe
 */
export function ChargesFixesForm({
  isOpen,
  onClose,
  onSubmit,
  chargeFix,
}: ChargesFixesFormProps) {
  useModalProtection(isOpen);
  const [nom, setNom] = useState('');
  const [categorie, setCategorie] = useState<CategorieChargeFix>('autre');
  const [montant, setMontant] = useState('');
  const [tauxTva, setTauxTva] = useState('20');
  const [frequence, setFrequence] = useState<FrequenceChargeFix>('mensuel');
  const [jourPrelevement, setJourPrelevement] = useState('5');
  const [isSaving, setIsSaving] = useState(false);

  const isEditing = !!chargeFix;

  // Pré-remplir le formulaire en mode édition
  useEffect(() => {
    if (chargeFix) {
      setNom(chargeFix.nom);
      setCategorie(chargeFix.categorie);
      setMontant(chargeFix.montant_ht.toString());
      setTauxTva((chargeFix.taux_tva ?? 20).toString());
      setFrequence(chargeFix.frequence);
      setJourPrelevement(chargeFix.jour_prelevement.toString());
    } else {
      // Reset pour nouvelle charge
      setNom('');
      setCategorie('autre');
      setMontant('');
      setTauxTva('20');
      setFrequence('mensuel');
      setJourPrelevement('5');
    }
  }, [chargeFix, isOpen]);

  const { toast } = useToast();

  const handleSubmit = async () => {
    const montantNum = parseFloat(montant);
    const jourNum = parseInt(jourPrelevement, 10);

    // Validation Zod
    const validation = chargeFixeSchema.safeParse({
      nom: nom.trim(),
      categorie,
      montant_ht: isNaN(montantNum) ? -1 : montantNum,
      taux_tva: parseFloat(tauxTva) || 20,
      frequence,
      jour_prelevement: isNaN(jourNum) ? 0 : jourNum,
    });

    if (!validation.success) {
      validation.error.issues.forEach(issue => {
        toast({ title: "Erreur", description: issue.message, variant: "destructive" });
      });
      return;
    }

    setIsSaving(true);
    try {
      await onSubmit(validation.data);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleMontantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Permettre uniquement les nombres et le point décimal
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setMontant(value);
  };

  const handleJourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Permettre uniquement les nombres entre 1 et 31
    const value = e.target.value.replace(/[^0-9]/g, '');
    const num = parseInt(value, 10);
    if (value === '' || (num >= 1 && num <= 31)) {
      setJourPrelevement(value);
    }
  };

  const isValid =
    nom.trim() !== '' &&
    parseFloat(montant) >= 0 &&
    parseInt(jourPrelevement, 10) >= 1 &&
    parseInt(jourPrelevement, 10) <= 31;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Modifier la charge fixe' : 'Ajouter une charge fixe'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Nom */}
          <div className="space-y-2">
            <Label htmlFor="nom">Nom</Label>
            <Input
              id="nom"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              placeholder="Ex: Loyer atelier"
            />
          </div>

          {/* Catégorie */}
          <div className="space-y-2">
            <Label htmlFor="categorie">Catégorie</Label>
            <Select
              value={categorie}
              onValueChange={(v) => setCategorie(v as CategorieChargeFix)}
            >
              <SelectTrigger id="categorie">
                <SelectValue placeholder="Sélectionner une catégorie" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CATEGORIES_LABELS) as CategorieChargeFix[]).map(
                  (cat) => (
                    <SelectItem key={cat} value={cat}>
                      {CATEGORIES_LABELS[cat]}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Montant HT + TVA */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="montant">Montant HT (€)</Label>
              <Input
                id="montant"
                type="text"
                inputMode="decimal"
                value={montant}
                onChange={handleMontantChange}
                placeholder="0,00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="taux_tva">TVA %</Label>
              <Select value={tauxTva} onValueChange={setTauxTva}>
                <SelectTrigger id="taux_tva">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0%</SelectItem>
                  <SelectItem value="5.5">5,5%</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="20">20%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {parseFloat(montant) > 0 && (
            <p className="text-xs text-muted-foreground">
              TTC : {(parseFloat(montant) * (1 + parseFloat(tauxTva) / 100)).toFixed(2)} €
            </p>
          )}

          {/* Fréquence */}
          <div className="space-y-2">
            <Label htmlFor="frequence">Fréquence</Label>
            <Select
              value={frequence}
              onValueChange={(v) => setFrequence(v as FrequenceChargeFix)}
            >
              <SelectTrigger id="frequence">
                <SelectValue placeholder="Sélectionner une fréquence" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(FREQUENCES_LABELS) as FrequenceChargeFix[]).map(
                  (freq) => (
                    <SelectItem key={freq} value={freq}>
                      {FREQUENCES_LABELS[freq]}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Jour de prélèvement */}
          <div className="space-y-2">
            <Label htmlFor="jour">Jour de prélèvement (1-31)</Label>
            <Input
              id="jour"
              type="text"
              inputMode="numeric"
              value={jourPrelevement}
              onChange={handleJourChange}
              placeholder="5"
            />
            <p className="text-xs text-muted-foreground">
              Jour du mois où le prélèvement est effectué.
              Si le jour dépasse le nombre de jours du mois, il sera ajusté au dernier jour.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !isValid}>
            {isSaving
              ? 'Enregistrement...'
              : isEditing
                ? 'Modifier'
                : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
